/**
 * Help API Client
 *
 * Functions for fetching documentation and interacting with the help chat.
 */

import { api } from '@/lib/api-client';
import { getStoredToken } from '@/lib/auth-client';
import { getDocInfoForRoute, normalizeRoute } from './routes';

export interface HelpDocument {
  route: string;
  title: string;
  description: string;
  content: string;
  keywords?: string[];
  lastUpdated?: string;
}

export interface ChatStreamEvent {
  type: 'token' | 'sources' | 'thinking' | 'done' | 'error';
  content?: string;
  sources?: Array<{
    id: string;
    title?: string;
    route?: string;
    score?: number;
  }>;
  error?: string;
}

/**
 * Fetch documentation for a specific route
 */
export async function getDocumentation(route: string): Promise<HelpDocument | null> {
  try {
    const normalizedRoute = normalizeRoute(route);
    const docInfo = getDocInfoForRoute(normalizedRoute);

    const response = await api.get<{ success: boolean; data: HelpDocument }>(
      `/api/v1/help/docs/${docInfo.docPath}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch documentation:', error);
    return null;
  }
}

/**
 * List all available documentation
 */
export async function listDocumentation(): Promise<HelpDocument[]> {
  try {
    const response = await api.get<{ success: boolean; data: HelpDocument[] }>(
      '/api/v1/help/docs'
    );

    if (response.success && response.data) {
      return response.data;
    }

    return [];
  } catch (error) {
    console.error('Failed to list documentation:', error);
    return [];
  }
}

/**
 * Send a chat message and stream the response
 */
export async function sendChatMessage(
  message: string,
  sessionId: string,
  currentRoute: string,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const token = getStoredToken();
  const response = await fetch('/api/v1/help/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message,
      sessionId,
      currentRoute: normalizeRoute(currentRoute),
    }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    onEvent({ type: 'error', error: errorText || 'Failed to send message' });
    return;
  }

  if (!response.body) {
    onEvent({ type: 'error', error: 'No response body' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();

          if (data === '[DONE]') {
            onEvent({ type: 'done' });
            continue;
          }

          try {
            const event = JSON.parse(data) as ChatStreamEvent;
            onEvent(event);
          } catch {
            // Not valid JSON, might be a heartbeat or other message
            if (data && data !== ':') {
              console.warn('Failed to parse SSE event:', data);
            }
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.startsWith('data: ')) {
      const data = buffer.slice(6).trim();
      if (data && data !== '[DONE]') {
        try {
          const event = JSON.parse(data) as ChatStreamEvent;
          onEvent(event);
        } catch {
          // Ignore parse errors
        }
      }
    }
  } catch (error) {
    if (signal?.aborted) {
      return; // Normal abort, don't report as error
    }
    onEvent({
      type: 'error',
      error: error instanceof Error ? error.message : 'Stream error',
    });
  }
}

/**
 * Trigger documentation re-indexing (admin only)
 */
export async function reindexDocumentation(): Promise<{
  success: boolean;
  documentsProcessed?: number;
  chunksCreated?: number;
  error?: string;
}> {
  try {
    const response = await api.post<{
      success: boolean;
      data: { documentsProcessed: number; chunksCreated: number };
    }>('/api/v1/help/index', {});

    if (response.success && response.data) {
      return {
        success: true,
        documentsProcessed: response.data.documentsProcessed,
        chunksCreated: response.data.chunksCreated,
      };
    }

    return { success: false, error: 'Indexing failed' };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
